import Axios, { AxiosRequestConfig } from 'axios';

import type CouponPropTypes from '@exp/components/products/couponPropTypes';

import { PRODUCT_SUGGESTIONS_VERSION, SEARCH_PROVIDERS } from './constants';
import type GuidelinesTypes from '../../store/components/guidelines/guidelinesTypes';
import type OrderHistoryTypes from '../../orderHistory/orderHistoryTypes';
import type ProductTypes from '../../product/productTypes';
import type SingleCheckoutTypes from '../../singleCheckout/singleCheckoutTypes';

enum HTTP_REQUEST_VERB {
  DELETE = 'DELETE',
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
}

const request = Axios.create({
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
    Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
  },
});

const send = (url: string, httpRequestVerb: HTTP_REQUEST_VERB = null, data = {}, extendedData = {}) => {
  let promiseResult: ApiResponse = null;

  // Check to see if the http method passed is valid.
  if (httpRequestVerb in HTTP_REQUEST_VERB) {
    promiseResult = (request[httpRequestVerb.toLowerCase()](url, data) as ApiResponse)
      .then(response => Promise.resolve(Object.assign(response, extendedData)))
      .catch((error) => {
        let enhancedData;
        if (error.response) {
          enhancedData = Object.assign(error.response.data, extendedData);
        } else {
          enhancedData = { errorResponse: error, error: true, ...extendedData };
        }
        return Promise.resolve({ data: enhancedData }) as ApiResponse;
      });
  } else {
    const msg = `API.js - send expects a valid HTTP request verb. (Passed: ${httpRequestVerb}, Expected: ${Object.keys(HTTP_REQUEST_VERB)})`;
    promiseResult = Promise.reject(new Error(msg, { cause: { data: msg } }));
  }

  return promiseResult;
};

const requestSimple = Axios.create({});

/**
 * Utility for ajax-like requests in react
 * Correctly resolves or rejects the promise, so that you can handle errors from your component.
 */
const sendSimple = (url: string, type: HTTP_REQUEST_VERB, requestBody?: object, config?: AxiosRequestConfig) => {
  const msg = `Xhr expected type of: ${Object.keys(HTTP_REQUEST_VERB)} but found type ${type}`;
  const data = requestBody || {};
  const userConfig = config || {};

  if (HTTP_REQUEST_VERB[type]) {
    return requestSimple[type.toLowerCase()](url, data, userConfig) as ApiResponse;
  }

  return Promise.reject(new Error(msg, { cause: { data: msg } }));
};

export const addFavorite =
  ({ organizationId, productCode, sku, storeId }): ApiResponse<ProductTypes.Favorite> =>
    send('/xapi/cart-services/ext/v1/favorites/item', HTTP_REQUEST_VERB.POST, { orgId: organizationId, productCode, sku, storeId });

export const autocompleteAddress =
  (query: SingleCheckoutTypes.AddressAutocompleteQuery): ApiResponse<SingleCheckoutTypes.AddressAutocompleteResponse> =>
    sendSimple('/xapi/address/ext/1.0/address/autocomplete', HTTP_REQUEST_VERB.GET, {
      params: {
        address1: query.address1 || null,
        address2: query.address2 || null,
        breakCache: query.breakCache || null,
        city: query.city || null,
        countryCode: query.countryCode || null,
        postalCode: query.postalCode || null,
        state: query.state || null,
        text: query.text || null,
      },
    });

export const cancelOrderSet = (orderSetId: number): ApiResponse<OrderHistoryTypes.OrderSetActionResult> =>
  sendSimple(`/xapi/order/ext/1.0/order/set/${orderSetId}/process`, HTTP_REQUEST_VERB.POST, { action: 'CANCEL' });

export const changeHistoryOrderSetShipping =
  (orderSetId: number, addressId: number): ApiResponse<OrderHistoryTypes.OrderSetActionResult> =>
    sendSimple(`/xapi/order/ext/1.0/order/set/${orderSetId}/process`, HTTP_REQUEST_VERB.POST, { action: 'CHANGE_SHIPPING', addressId });

export const claimCoupon = ({ couponId }): ApiResponse<void> =>
  sendSimple(`/xapi/cart-services/ext/1.0/coupons/${couponId}/claim`, HTTP_REQUEST_VERB.POST);

export const cancelProductNotification = (organizationId, sku) =>
  send(`/xapi/cart-services/ext/v1/${organizationId}/product-notification/cancel`, HTTP_REQUEST_VERB.PUT, { organizationId, sku });

export const deleteCartItem = (cartItemId, storeId) =>
  send(`/xapi/cart-services/ext/v1/cart-item/${cartItemId}/${storeId}`, HTTP_REQUEST_VERB.DELETE);

export const getCouponsForStore = ({
  orgStoreIds = {},
  status = 'ACTIVE',
  types,
}): ApiResponse<{ content: CouponPropTypes.couponType[] }> =>
  sendSimple('/xapi/coupon/ext/2.0/public/search', HTTP_REQUEST_VERB.POST, {
    orgStoreIds,
    status,
    types,
  });

export const getCouponsForUser = ({
  issuedStatuses = ['VALID'],
  orgStoreExactMatch = false,
  orgStoreIds = {},
  status = 'ACTIVE',
}): ApiResponse<CouponPropTypes.couponDataType> =>
  sendSimple('/xapi/coupon/ext/2.0/user/all', HTTP_REQUEST_VERB.POST, {
    orgStoreExactMatch,
    orgStoreIds,
    issuedStatuses,
    status,
  });

export const extendIssuedCoupon = (couponId: number) =>
  sendSimple(`/xapi/coupon/ext/2.0/user/issued/${couponId}/extend`, HTTP_REQUEST_VERB.POST);

export const getCampaignAssetData = (campaignId) =>
  send(`/xapi/campaign/pub/v1/campaignAsset/${campaignId}/`, HTTP_REQUEST_VERB.GET);

export const getCampaignByStoreId = (storeId) =>
  send(`/shop/campaignLookup/${storeId}`, HTTP_REQUEST_VERB.GET);

export const getCampaignsForStoreIds = (storeIds): ApiResponse<Record<string, ProductTypes.CampaignContent>> =>
  send(`/shop/campaignsLookup?storeIds=${storeIds.join(',')}`, HTTP_REQUEST_VERB.GET);

export const getCart = (cartId) =>
  send(`/xapi/cart-services/ext/v1/cart/${cartId}`, HTTP_REQUEST_VERB.GET);

export const getCartSet = (key) =>
  sendSimple(`/xapi/checkout-services/ext/v1/cart-set?key=${key}`, HTTP_REQUEST_VERB.GET);

export const getCoupon = (id) =>
  sendSimple(`/xapi/checkout-services/ext/v1/coupon/${id}`, HTTP_REQUEST_VERB.GET);

export const fetchGroupsWithCoupons = (couponUse) =>
  sendSimple('/xapi/checkout-services/ext/v2/carts', HTTP_REQUEST_VERB.POST, couponUse);

export const getCartSetShipInfo = (key) =>
  sendSimple(`/xapi/checkout-services/ext/v3/ship-options?key=${key}`, HTTP_REQUEST_VERB.GET);

export const getTopProducts = () =>
  send('/xapi/store-services/pub/v1/preview/top-products', HTTP_REQUEST_VERB.GET);

export const getHistoryOrder = ({ orderId }: { orderId: number }): ApiResponse<OrderHistoryTypes.OrderDetail> =>
  sendSimple(`/xapi/order-info-services/ext/v2/history/order/${orderId}`, HTTP_REQUEST_VERB.GET);

export const getHistoryOrders =
  ({ page, orgId }: { page?: number; orgId?: number }): ApiResponse<OrderHistoryTypes.PagedOrderList> => {
    const pageSize = 24;

    let historyUri = `/xapi/order-info-services/ext/5.0/history?page=${page || 0}&size=${pageSize}`;

    if (orgId) {
      historyUri += `&orgId=${orgId}`;
    }

    return send(historyUri, HTTP_REQUEST_VERB.GET);
  };

export const getHistoryOrderSet = (orderSetId: number): ApiResponse<OrderHistoryTypes.OrderSet> =>
  sendSimple(`/xapi/order-info-services/ext/3.0/set/${orderSetId}`, HTTP_REQUEST_VERB.GET);

export const getHistoryOrderSetShippingOptions =
  (orderSetId: number): ApiResponse<OrderHistoryTypes.ChangeShippingAddressResponse> =>
    sendSimple(`/xapi/order/ext/1.0/order/set/${orderSetId}/process/CHANGE_SHIPPING`, HTTP_REQUEST_VERB.GET);

export const getOrderOrgs = (): ApiResponse<OrderHistoryTypes.Organization[]> =>
  sendSimple('/xapi/order-info-services/ext/v1/history/orgs', HTTP_REQUEST_VERB.GET);

export const getCommunityOrganization = (orgId) =>
  send(`/xapi/community/ext/1.0/page/org/${orgId}`, HTTP_REQUEST_VERB.GET);

export const getOrganization = (orgId) =>
  send(`/xapi/organization/pub/v1/organization/${orgId}`, HTTP_REQUEST_VERB.GET);

export const getOrganizationLessons = (orgId, {
  page = 0,
  size = 3,
  sortDirection = 'DESC',
  sortField = 'modifiedTime',
} = {}) =>
  sendSimple('/xapi/search/ext/2.0/search', HTTP_REQUEST_VERB.POST, {
    requestedProviders: [SEARCH_PROVIDERS.MODULE],
    searchConfiguration: {
      filters: {
        ORGANIZATION: [orgId],
      },
      maxResults: size,
      sortDirection,
      sortField,
      startResults: page * size,
    },
  });

export const getOrganizationStores = (orgId, ignoreCache = false) =>
  send(`/xapi/store-services/ext/v1/${orgId}/organization/stores?countProducts=true&ignoreCache=${ignoreCache}`, HTTP_REQUEST_VERB.GET);

export const getPendingProductNotifications =
  (orgId, storeId, catalogProductId): ApiResponse<ProductTypes.ProductNotification[]> =>
    send(`/xapi/cart-services/ext/v1/${orgId}/product-notifications/${storeId}/${catalogProductId}`, HTTP_REQUEST_VERB.GET);

export const getProductAffiliatePrices = (data) =>
  sendSimple('/xapi/ext-product/ext/1.0/variant/external/public/search', HTTP_REQUEST_VERB.POST, data);

export const getProductDetails = (orgId, productCode): ApiResponse<ProductTypes.ProductDetail> =>
  send(`/xapi/store-services/ext/v2/${orgId}/product-detail?productCode=${encodeURIComponent(productCode)}`, HTTP_REQUEST_VERB.GET);

export const getProductFavorite = (orgId: number, productCode: string): ApiResponse<ProductTypes.Favorite> =>
  sendSimple(`/xapi/cart-services/ext/v1/favorites/product?orgId=${orgId}&productCode=${productCode}`, HTTP_REQUEST_VERB.GET);

export const getProductPreview = (orgId, productCode) =>
  sendSimple(`/xapi/store-services/pub/v1/preview/${orgId}/product-detail?productCode=${encodeURIComponent(productCode)}&activeOnly=false`, HTTP_REQUEST_VERB.GET);

export const getPubProductPreview = (orgId, productCode) =>
  sendSimple(`/xapi/store-services/pub/v1/preview/${orgId}/product-detail?productCode=${encodeURIComponent(productCode)}&activeOnly=false`, HTTP_REQUEST_VERB.GET);

export const getProductModules = (productCode, organizationId): ApiResponse<ProductTypes.Module[]> =>
  send(`/xapi/user-knowledge/ext/1.0/moduleProduct/org/${organizationId}?productCode=${encodeURIComponent(productCode)}`, HTTP_REQUEST_VERB.GET);

export const getProductSuggestions =
  ({ orgId, productCode, productLimit = 3, version = PRODUCT_SUGGESTIONS_VERSION }):
  ApiResponse<{ resultItems: ProductTypes.ProductCardData[] }> =>
    sendSimple(`/xapi/store-services/ext/v1/suggest/organization/${orgId}/product`, HTTP_REQUEST_VERB.GET, { params: {
      productCode,
      productLimit,
      version,
    } });

export const getCartSuggestions = () =>
  sendSimple('/xapi/cart-services/ext/1.0/cart/suggestions', HTTP_REQUEST_VERB.GET);

export const getCartSuggestionsByProduct = (orgId, productCode) =>
  sendSimple('/xapi/store-services/ext/v1/suggest/products', HTTP_REQUEST_VERB.POST, {
    productLimit: 12,
    sourceProducts: {
      [orgId]: [productCode],
    },
  });

export const getProductsWithCategories = (searchConfiguration, searchTerm) =>
  send('/xapi/store-services/ext/v1/stores/search/products', HTTP_REQUEST_VERB.POST, { searchTerm, searchConfiguration });

export const getOrderBillingInfo = (orderId) =>
  sendSimple(`/xapi/checkout-services/ext/v1/order/pay-config/${orderId}`, HTTP_REQUEST_VERB.GET);

export const getCartSetBillingInfo = (groupKey) =>
  sendSimple(`/xapi/checkout-services/ext/v3/checkout/pay-config?key=${groupKey}`, HTTP_REQUEST_VERB.GET);

/* accepts a com.experticity.checkoutservices.dto.order.CheckoutCartSetDto
{key: 'id,id,id,id', shipping: {address?: addressDto, teamId?: number,
   methods: [{cartId: number, methodId: number}] }} */
export const validateShipInfo = (costDto) =>
  sendSimple('/xapi/checkout-services/ext/v3/review-costs', HTTP_REQUEST_VERB.POST, costDto);

/** @throws {ApiError<SingleCheckoutTypes.ReviewApiResponseError>} */
export const getReviewInfoV5 =
  (reviewRequest: SingleCheckoutTypes.ReviewApiRequest): ApiResponse<SingleCheckoutTypes.ReviewApiResponse> =>
    sendSimple('/xapi/checkout-services/ext/v5/review', HTTP_REQUEST_VERB.POST, reviewRequest);

export const createAddress = (address: Partial<SingleCheckoutTypes.SavedShippingAddress>) =>
  sendSimple('/xapi/user/ext/1.0/address/shipping', HTTP_REQUEST_VERB.POST, address);

export const removeAddress = (addressId: number) =>
  sendSimple(`/xapi/user/ext/1.0/address/shipping/${addressId}`, HTTP_REQUEST_VERB.DELETE);

export const updateAddress = (address: SingleCheckoutTypes.SavedShippingAddress) =>
  sendSimple(`/xapi/user/ext/1.0/address/shipping/${address.id}`, HTTP_REQUEST_VERB.POST, address);

export const validateAddress = (address: SingleCheckoutTypes.Address) =>
  sendSimple('/xapi/address/ext/1.0/address/validate', HTTP_REQUEST_VERB.POST, address);

/** @throws {ApiError<SingleCheckoutTypes.ReviewApiResponseError>} */
export const createOrder =
  (reviewRequest: SingleCheckoutTypes.ReviewApiRequest): ApiResponse<SingleCheckoutTypes.PlaceOrderApiResponse> =>
    sendSimple('/xapi/checkout-services/ext/v1/order-set', HTTP_REQUEST_VERB.POST, reviewRequest);

export const updatePayment = (orderId, payment) =>
  sendSimple('/xapi/checkout-services/ext/v3/order/update-payment', HTTP_REQUEST_VERB.POST, {
    orderId,
    payment,
  });

/** @deprecated, used in updatePayment */
export const tokenizePayment = (addressDTO, paymentDTO, configKey) => {
  const url = `${context.secureServiceUrl}/xapi/secure/pub/v1/payment/tokenize`;

  // Gross, but the billing address dto is only almost an address dto,
  // so we'll pretend it is one until we have to care. Which is now.
  const convertPhoneToBillingPhone = (shipAddressDto) => {
    const billingAddressDto = {
      billingPhone: shipAddressDto.phone,
      ...shipAddressDto,
    };
    delete billingAddressDto.phone;
    return billingAddressDto;
  };

  return sendSimple(url, HTTP_REQUEST_VERB.POST, {
    billingAddress: convertPhoneToBillingPhone(addressDTO),
    payment: paymentDTO,
    configuration: configKey,
  });
};

/** @throws {ApiError<SingleCheckoutTypes.ReviewApiResponseError>} */
export const tokenizeCCPayment = (
  billingAddress: SingleCheckoutTypes.BillingAddress,
  payment: SingleCheckoutTypes.RawCreditCardDetails,
  configuration: SingleCheckoutTypes.CreditCardPaymentConfiguration,
) : ApiResponse<SingleCheckoutTypes.TokenizedPaymentString> => {
  const url = `${context.secureServiceUrl}/xapi/secure/pub/v1/payment/tokenize`;
  return sendSimple(url, HTTP_REQUEST_VERB.POST, { billingAddress, payment, configuration });
};

export const getStoreDiscountAvailable = (storeId, ignoreCache = false) =>
  send(`/xapi/store-services/ext/v1/discount-available/${storeId}?ignoreCache=${ignoreCache}`, HTTP_REQUEST_VERB.GET);

export const getMultipleStoreDiscountAvailable = (storeIds, ignoreCache = false) =>
  send(`/xapi/store-services/ext/v1/discount-available?storeIds=${[storeIds.join(',')]}&ignoreCache=${ignoreCache}`, HTTP_REQUEST_VERB.GET);

export const getStoreGuidelines =
  (storeIds: number[]): ApiResponse<GuidelinesTypes.Guidelines[] | GuidelinesTypes.ErrorCodes> =>
    send(`/xapi/cart-services/ext/v1/guidelines?storeIds=${storeIds.join()}`, HTTP_REQUEST_VERB.GET);

export const postCartItem = (quantity, skuId, storeId) =>
  send('/xapi/cart-services/ext/v1/cart-item', HTTP_REQUEST_VERB.POST, { quantity, skuId, storeId });

export const postProductNotificationRequest = (storeId, skuId) =>
  send('/xapi/cart-services/ext/v1/product-notification', HTTP_REQUEST_VERB.POST, { storeId, skuId });

export const removeFileAttachment = (fileId: string): ApiResponse<void> =>
  sendSimple(`/xapi/xds/ext/v1/orderAttachment/${fileId}`, HTTP_REQUEST_VERB.DELETE);

export const resendVerification = (emailAddress) =>
  sendSimple(`/xapi/user/ext/1.0/email/${emailAddress}/requestVerification`, HTTP_REQUEST_VERB.POST, {});

export const uploadFile = (cartId: number, file: File): ApiResponse<SingleCheckoutTypes.AttachedFile> => {
  const url = `/xapi/xds/ext/v1/orderAttachment/cart/${cartId}`;
  const formData = new FormData();
  formData.append('fileData', file);
  return sendSimple(url, HTTP_REQUEST_VERB.POST, formData);
};

export const getAttachmentsByCart = (cartId: number): ApiResponse<SingleCheckoutTypes.AttachedFile[]> => {
  const url = `/xapi/xds/ext/v1/orderAttachment/cart/${cartId}`;
  return sendSimple(url, HTTP_REQUEST_VERB.GET);
};

export const getAllOrderAttachments = (): ApiResponse<OrderHistoryTypes.OrderAttachmentMap> => {
  const url = '/xapi/xds/ext/v1/orderAttachment/orders';
  return sendSimple(url, HTTP_REQUEST_VERB.GET);
};

export const updateCartItem = (quantity, sku, storeId) =>
  send('/xapi/cart-services/ext/v1/cart-item', HTTP_REQUEST_VERB.PUT, { quantity, sku, storeId });

export const getRatingBreakdownsForProducts = (orgId, productCodes = []) =>
  send('/xapi/voice/ext/1.0/review/breakdown', HTTP_REQUEST_VERB.POST, {
    entityIds: productCodes,
    entityOwnerId: orgId,
    grouping: 'ENTITY_ID',
  }, { parameters: { orgId, productCodes } });

export const getRatingSummaryForProduct = (orgId, productCode): ApiResponse<ProductTypes.RatingSummary> =>
  sendSimple(`/xapi/voice/pub/1.0/review/summary?entityOwnerId=${orgId}&entityId=${productCode}&entityAlternateIds=${productCode}`, HTTP_REQUEST_VERB.GET);

export const getRecommendationsForUser = (userId: number): ApiResponse<OrderHistoryTypes.UserRecommendationResponse> =>
  sendSimple(`/xapi/voice/ext/1.0/review?userId=${userId}`, HTTP_REQUEST_VERB.GET);

export const getPubRecommendationsForProduct = (orgId, productCode) =>
  sendSimple('/xapi/voice/pub/1.0/socialism/search', HTTP_REQUEST_VERB.POST, {
    entityIds: [productCode],
    entityOwnerId: orgId,
  });

export const getGuidelinesByPart = (storeIds, parts) => {
  const params = new URLSearchParams({ storeIds, parts });
  return sendSimple(`/xapi/store-services/ext/v3/guidelines/parts?${params.toString()}`, HTTP_REQUEST_VERB.GET);
};

export const getPromoStores = () =>
  sendSimple('/xapi/store-services/ext/v1/promotion/stores', HTTP_REQUEST_VERB.GET);

export const getBrandContent = (orgId) =>
  sendSimple(`/xapi/knowledge-content/ext/1.0/contentFolders/org/${orgId}/list`, HTTP_REQUEST_VERB.GET);

export const getNextStoreModule = (storeIds) =>
  sendSimple(`/xapi/user-knowledge/ext/1.0/learn/next?storeId=${storeIds.join(',')}`, HTTP_REQUEST_VERB.GET);

export const getTaxonomyNames = (taxonomyIds) =>
  sendSimple(`/xapi/store-services/ext/v1/taxonomy/names?taxonomyIds=${taxonomyIds.join()}`, HTTP_REQUEST_VERB.GET);

export const getSaveForLater = () =>
  sendSimple('/xapi/cart-services/ext/v1/saveforlater', HTTP_REQUEST_VERB.GET);

export const addSaveForLater = (storeId, sku, quantity) =>
  sendSimple('/xapi/cart-services/ext/v1/saveforlater/item', HTTP_REQUEST_VERB.POST, {
    storeId, sku, quantity,
  });

export const deleteSaveForLater = (savedItemId) =>
  sendSimple(`/xapi/cart-services/ext/v1/saveforlater/item/${savedItemId}`, HTTP_REQUEST_VERB.DELETE);

export const moveSavedToCart = (savedItemId) =>
  sendSimple(`/xapi/cart-services/ext/v1/saveforlater/item/${savedItemId}/cart`, HTTP_REQUEST_VERB.PUT);

export const moveCartToSaved = (cartItemId) =>
  sendSimple(`/xapi/cart-services/ext/v1/saveforlater/cartitem/${cartItemId}`, HTTP_REQUEST_VERB.PUT);

export const getUserMetadataByKey = (key) =>
  sendSimple(`/xapi/user/ext/1.0/metadata/${key}`, HTTP_REQUEST_VERB.GET);

export const deleteSavedPayment = (savedPaymentId: SingleCheckoutTypes.SavedPaymentId): ApiResponse<boolean> =>
  sendSimple(`/xapi/checkout-services/ext/v1/payment/saved/${savedPaymentId}`, HTTP_REQUEST_VERB.DELETE);

export const removeFavorite = (itemId: number): ApiResponse<void> =>
  sendSimple(`/xapi/cart-services/ext/v1/favorites/item/${itemId}`, HTTP_REQUEST_VERB.DELETE);

export const getUnlockPathsForStore = (storeId) =>
  sendSimple('/xapi/campaign/ext/v1/campaign/unlock/options', HTTP_REQUEST_VERB.POST, { storeIds: [storeId] });

export const continueProductConversation = (data: {
  conversationId: string,
  message: string,
}): ApiResponse<ProductTypes.ProductConversation> =>
  sendSimple(`/xapi/ai/ext/1.0/qanda/conversations/${data.conversationId}/messages`, HTTP_REQUEST_VERB.POST, {
    message: data.message,
  });

export const fetchProductConversationSuggestions = (data: {
  orgId: number,
  productCode: string,
}): ApiResponse<ProductTypes.ProductConversationSuggestions> => {
  const params = new URLSearchParams({
    entityId: `${data.orgId}:${data.productCode}`,
    entityType: 'PRODUCT',
  });
  return sendSimple(`/xapi/ai/ext/1.0/qanda/suggestions?${params.toString()}`, HTTP_REQUEST_VERB.GET);
};

export const startProductConversation = (data: {
  message?: string,
  orgId: number,
  productCode: string,
}): ApiResponse<ProductTypes.ProductConversation> =>
  sendSimple('/xapi/ai/ext/1.0/qanda/conversations', HTTP_REQUEST_VERB.POST, {
    entityId: `${data.orgId}:${data.productCode}`,
    entityType: 'PRODUCT',
    message: data.message,
  });

export const submitProductConversationFeedback = (data: {
  accepted: boolean,
  conversationId: string,
}): ApiResponse<ProductTypes.ProductConversation> =>
  sendSimple(`/xapi/ai/ext/1.0/qanda/conversations/${data.conversationId}/feedback`, HTTP_REQUEST_VERB.POST, {
    accepted: data.accepted,
  });
